/**
 * Underlined tab strip + List/Both/Map view toggle (VOY-475).
 *
 * Renders the new design chrome from Search Plus.html:
 *   - .sr-tabs row: Flights N · Hotels N · Itinerary Nd, with count badges
 *     in a mono font that turns accent on the active tab.
 *   - .sr-view-toggle: pill row on the right with List / Both / Map.
 *
 * Tab visibility is driven from the caller (search chips → which tabs
 * appear). The Itinerary tab carries a day-count badge ("7d"); flights
 * and hotels show plain counts.
 */
import type { ReactNode } from 'react'

export type ResultTabKey = 'flights' | 'hotels' | 'itinerary'
export type ResultViewMode = 'list' | 'both' | 'map'

export interface SearchResultsTabsProps {
  active: ResultTabKey
  onChange: (next: ResultTabKey) => void
  tabs: Array<{
    key: ResultTabKey
    label: string
    /** Display string for the count badge — caller formats (e.g., "247" or "7d"). */
    count?: string | number
    /** Show a spinner glyph next to the label while a tab is loading. */
    loading?: boolean
  }>
  /** Right-hand VIEW toggle. Omit to hide the toggle entirely. */
  view?: {
    value: ResultViewMode
    onChange: (next: ResultViewMode) => void
    /** Restrict to a subset (e.g., only show Map on the Hotels tab). */
    allowed?: ResultViewMode[]
  }
  /** Optional trailing slot rendered to the right of the view toggle. */
  trailing?: ReactNode
}

const ALL_VIEWS: ResultViewMode[] = ['list', 'both', 'map']
const VIEW_LABELS: Record<ResultViewMode, string> = { list: 'List', both: 'Both', map: 'Map' }

export function SearchResultsTabs({ active, onChange, tabs, view, trailing }: SearchResultsTabsProps) {
  const views = view?.allowed ?? ALL_VIEWS
  // ArrowLeft/Right move focus across tabs per the WAI-ARIA Tabs APG.
  // Home/End jump to the first/last tab. Wraps at the edges.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, currentKey: ResultTabKey) => {
    if (tabs.length === 0) return
    const idx = tabs.findIndex((t) => t.key === currentKey)
    if (idx < 0) return
    let nextIdx: number | null = null
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % tabs.length
    else if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + tabs.length) % tabs.length
    else if (e.key === 'Home') nextIdx = 0
    else if (e.key === 'End') nextIdx = tabs.length - 1
    if (nextIdx == null) return
    e.preventDefault()
    const nextKey = tabs[nextIdx].key
    // Move DOM focus BEFORE state update so the new tab is focused
    // regardless of how React schedules the re-render. A rAF/useEffect
    // callback would race with concurrent rendering and sometimes leave
    // focus on the previous tab.
    document.getElementById(`sr-tab-${nextKey}`)?.focus()
    onChange(nextKey)
  }
  return (
    // Outer wrapper is a layout container, not a landmark — the
    // `tablist` below is the meaningful ARIA structure.
    <div className="sr-tabs-row">
      <div className="sr-tabs" role="tablist" aria-label="Search results">
        {tabs.map((t) => {
          const isActive = t.key === active
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`sr-pane-${t.key}`}
              id={`sr-tab-${t.key}`}
              tabIndex={isActive ? 0 : -1}
              className={`sr-tab${isActive ? ' is-active' : ''}`}
              onClick={() => onChange(t.key)}
              onKeyDown={(e) => handleKeyDown(e, t.key)}
            >
              {t.label}
              {t.count != null && t.count !== '' && (
                <span className="sr-tab-count">{t.count}</span>
              )}
              {t.loading && (
                <span className="sr-tab-spinner" aria-hidden="true" />
              )}
            </button>
          )
        })}
      </div>
      {(view || trailing) && (
        <div className="sr-tabs-right">
          {view && (
            <>
              <span className="sr-view-label">VIEW</span>
              <div className="sr-view-toggle" role="group" aria-label="View mode">
                {views.map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={view.value === m}
                    className={`sr-view-btn${view.value === m ? ' is-active' : ''}`}
                    onClick={() => view.onChange(m)}
                  >
                    {VIEW_LABELS[m]}
                  </button>
                ))}
              </div>
            </>
          )}
          {trailing}
        </div>
      )}
    </div>
  )
}
